import { afterNextRender, Component, DestroyRef, DOCUMENT, ElementRef, effect, inject, viewChild } from '@angular/core';
import { ToastService } from './toast.service';
import { HlmIconComponent } from './icon.component';
import { cn } from './cn';

/**
 * Toast stack.
 *
 * @ticket EB-4397 — CDK 21 opens every dialog as a `popover` in the browser top
 * layer, which paints above any `z-index`. A plain fixed stack therefore rendered
 * behind open dialogs (the "stuck dialog" symptom of EB-4396). The stack is a manual
 * popover itself and is re-shown on every new toast: the top layer is ordered by
 * show time, so the latest toast always lands above the dialog that raised it.
 *
 * The stack element is moved to a direct child of `<body>`: an open CDK dialog sets
 * `aria-hidden` on every `<body>` child except the overlay container and elements
 * carrying a `popover` attribute — the app root included, so a stack left inside it
 * would stay hidden from screen readers while a dialog is open (code review M5).
 * Without the popover API the CDK falls back to z-index stacking, and so does this;
 * a throwing popover call (older engines) is swallowed for the same reason.
 */
@Component({
  selector: 'hlm-toast-container',
  standalone: true,
  imports: [HlmIconComponent],
  template: `
    <div
      #stack
      data-toast-stack
      popover="manual"
      class="fixed inset-auto bottom-6 right-6 z-[10000] m-0 flex flex-col gap-2 overflow-visible border-0 bg-transparent p-0 text-inherit"
    >
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          [class]="toastClass(toast.tone)"
          [attr.role]="toast.tone === 'error' ? 'alert' : 'status'"
          [attr.aria-live]="toast.tone === 'error' ? 'assertive' : 'polite'"
        >
          @if (toast.tone === 'error') {
            <hlm-icon name="lucideCircleX" size="1.15rem" class="flex-none" />
          } @else if (toast.tone === 'success') {
            <hlm-icon name="lucideCircleCheck" size="1.15rem" class="flex-none" />
          } @else if (toast.tone === 'info') {
            <hlm-icon name="lucideInfo" size="1.15rem" class="flex-none" />
          }
          <span class="flex-1">{{ toast.message }}</span>
          @if (toast.action) {
            <button
              type="button"
              (click)="toastService.triggerAction(toast.id)"
              class="flex-none font-semibold underline-offset-2 hover:underline"
            >{{ toast.action }}</button>
          }
          <button
            type="button"
            (click)="toastService.dismiss(toast.id)"
            class="flex-none rounded-md p-0.5 transition-colors hover:bg-card/20"
            aria-label="Dismiss"
          >
            <hlm-icon name="lucideX" size="1rem" />
          </button>
        </div>
      }
    </div>
  `,
})
export class HlmToastContainerComponent {
  readonly toastService = inject(ToastService);

  private readonly stack = viewChild.required<ElementRef<HTMLElement>>('stack');
  private readonly document = inject(DOCUMENT);
  private previousCount = 0;

  constructor() {
    let moved: HTMLElement | null = null;
    afterNextRender(() => {
      moved = this.stack().nativeElement;
      this.document.body.appendChild(moved);
      // Moving a showing popover hides it — re-show toasts raised before the first
      // render (e.g. during bootstrap) (review run 2, N2).
      if (this.toastService.toasts().length > 0) this.reshow(moved);
    });
    inject(DestroyRef).onDestroy(() => moved?.remove());

    effect(() => {
      const count = this.toastService.toasts().length;
      const element = this.stack().nativeElement;
      const grew = count > this.previousCount;
      this.previousCount = count;
      if (count === 0) {
        this.safely(() => element.hidePopover());
      } else if (grew) {
        this.reshow(element);
      }
    });
  }

  /** hide + show moves an already-open popover to the top of the top layer. */
  private reshow(element: HTMLElement): void {
    this.safely(() => {
      element.hidePopover();
      element.showPopover();
    });
  }

  /** Popover calls must never break change detection; without the API z-index applies. */
  private safely(call: () => void): void {
    if (typeof HTMLElement.prototype.showPopover !== 'function') return;
    try {
      call();
    } catch {
      // popover state errors (older engines) — the z-index fallback still applies
    }
  }

  toastClass(tone: string): string {
    const base = 'flex items-center gap-3.5 rounded-2xl px-6 py-4 text-base font-medium shadow-xl backdrop-blur-sm animate-[cmt-fade-up_0.3s_ease-out] min-w-[360px] max-w-[520px]';
    switch (tone) {
      case 'error':
        return cn(base, 'bg-destructive text-primary-foreground');
      case 'success':
        return cn(base, 'bg-success text-primary-foreground');
      // Amber: a neutral statement that still carries across the screen. The default
      // near-black toast reads as chrome and gets missed (EB-4170 review, M8).
      case 'info':
        return cn(base, 'bg-accent text-accent-foreground');
      default:
        return cn(base, 'bg-foreground text-background');
    }
  }
}
